
import React from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Edit, Trash } from "lucide-react";
import { WorkoutType } from "@/models/WorkoutType";
import { useTranslation } from "react-i18next";

interface WorkoutTypeCardProps {
  workoutType: WorkoutType;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  canManage: boolean;
}

const WorkoutTypeCard: React.FC<WorkoutTypeCardProps> = ({ 
  workoutType, 
  onEdit, 
  onDelete, 
  canManage 
}) => {
  const {t} = useTranslation();

  return (
    <Card className="h-full">
      <CardHeader className="pb-2 md:pb-3 p-3 md:p-6">
        <div className="flex justify-between items-start gap-2">
          <div className="flex items-center space-x-2 min-w-0 flex-1">
            <div 
              className="w-3 h-3 md:w-4 md:h-4 rounded-full flex-shrink-0" 
              style={{ backgroundColor: workoutType.color }} 
            />
            <CardTitle className="text-sm md:text-lg font-medium truncate">
              {workoutType.name}
            </CardTitle>
          </div>
          <Badge 
            variant={workoutType.is_active ? "default" : "secondary"}
            className="text-xs flex-shrink-0"
          >
            {workoutType.is_active ? t('common.active') : t('common.inactive')}
          </Badge>
        </div>
        {workoutType.description && (
          <CardDescription className="line-clamp-2 text-xs md:text-sm mt-1">
            {workoutType.description}
          </CardDescription>
        )}
      </CardHeader>
      <CardContent className="p-3 md:p-6 pt-0">
        <div className="flex justify-between items-center">
          <div />
          {canManage && (
            <div className="flex space-x-1 md:space-x-2">
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => onEdit(workoutType.id)}
                className="h-7 w-7 md:h-8 md:w-8"
              >
                <Edit className="h-3 w-3 md:h-4 md:w-4" />
              </Button>
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => onDelete(workoutType.id)}
                className="h-7 w-7 md:h-8 md:w-8 text-destructive hover:text-destructive"
              >
                <Trash className="h-3 w-3 md:h-4 md:w-4" />
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default WorkoutTypeCard;
